import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import {
  getFinanceReklamkyPartners,
  getFinanceReklamkySummary,
  getFinanceReklamkyTimeseries,
  type AdRevPartner,
  type AdRevSummary,
  type AdRevCountryMtd,
  type AdRevTimeseriesResult,
  type AdRevPeriod,
  type AdRevGranularity,
} from '../api/rondo';
import { useTabParam } from '../shell/useTabParam';
import { ServiceBar } from '../shell/ServiceBar';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import './finance-reklamky.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);
const nf = new Intl.NumberFormat('cs-CZ');

type SubTab = 'zakladni' | 'prehled' | 'vypis';
const TABS: readonly SubTab[] = ['zakladni', 'prehled', 'vypis'];
type Country = 'ALL' | 'CZ' | 'SK' | 'RO';

const COUNTRY_META: Record<Country, { flag: string; name: string; host: string }> = {
  ALL: { flag: '🌐', name: 'Celkem (CZ+SK+RO)', host: '' },
  CZ: { flag: '🇨🇿', name: 'Česko', host: 'ro.rondo.cz' },
  SK: { flag: '🇸🇰', name: 'Slovensko', host: 'ro.rondogo.sk' },
  RO: { flag: '🇷🇴', name: 'Rumunsko', host: 'ro.rondogo.ro' },
};
const COUNTRY_LABELS: Record<Country, string> = { ALL: 'Celkem', CZ: 'Česko', SK: 'Slovensko', RO: 'Rumunsko' };

const fmtKc = (n: number) => `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`;
const fmtN = (n: number) => Math.round(n).toLocaleString('cs-CZ');
const ecpmOf = (rev: number, imp: number) => (imp > 0 ? fmtKc((rev / imp) * 1000) : '-');

function fmtDelta(curr: number, prev: number): ReactNode {
  if (!prev) return <span className="text-muted">-</span>;
  const pct = ((curr - prev) / prev) * 100;
  const up = pct >= 0;
  return <span className={up ? 'text-success' : 'text-danger'}>{up ? '▲' : '▼'} {up ? '+' : ''}{pct.toFixed(1)}%</span>;
}
const Dot = () => (
  <span title="Dopočítává se (dodavatel doplňuje ~4 dny) - nefinální" style={{ color: '#f59e0b', marginLeft: 6, fontSize: '1.05em', lineHeight: 1 }}>●</span>
);

const partnerOptions = (partners: AdRevPartner[], merged = false) => [
  { value: 'all', label: 'Všichni' },
  ...(merged ? [{ value: 'merged', label: 'Sloučit (součet)' }] : []),
  ...partners.map((p) => ({ value: p.id, label: p.name })),
];
const countryOptions = [
  { value: 'ALL', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ' },
  { value: 'SK', label: '🇸🇰 SK' }, { value: 'RO', label: '🇷🇴 RO' },
];

export function FinanceReklamkyPage() {
  const [tab, setTab] = useTabParam<SubTab>('tab', TABS, 'zakladni');
  const [partners, setPartners] = useState<AdRevPartner[]>([]);

  useEffect(() => {
    let cancelled = false;
    getFinanceReklamkyPartners().then((l) => !cancelled && setPartners(l)).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="rondo-reklamky rondo-arc-reklamky p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">FINANCE</div><h1>Reklamky</h1><p>Výnosy z reklamních sítí napříč trhy a dodavateli</p></div></header>
      <ArcTabs label="Reklamky" items={[{ value: 'zakladni', label: 'Základní' }, { value: 'prehled', label: 'Přehled' }, { value: 'vypis', label: 'Výpis' }]} value={tab} onChange={setTab} className="rondo-reklamky-arc-tabs" />
      {tab === 'zakladni' ? <ZakladniTab partners={partners} /> : tab === 'prehled' ? <PrehledTab partners={partners} /> : <VypisTab partners={partners} />}
    </section>
  );
}

function ZakladniTab({ partners }: { partners: AdRevPartner[] }) {
  const [partner, setPartner] = useState('all');
  const [data, setData] = useState<AdRevSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getFinanceReklamkySummary(partner)
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [partner]);

  const period = data ? new Date(data.mtdPeriod).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' }) : '';
  const lastData = data?.lastDataDate
    ? new Date(data.lastDataDate).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' })
    : null;

  return (
    <>
      <ServiceBar variant="seg" selects={[{ label: 'Dodavatel', value: partner, onChange: setPartner, options: partnerOptions(partners) }]} onReset={() => setPartner('all')} right={<span className="rondo-arc-reklamky-period">{loading ? 'Načítám…' : `Aktuální měsíc · ${period}${lastData ? ` · data k ${lastData}` : ''}`}</span>} />
      {error ? <PageState title="Reklamky se nepodařilo načíst" message={error} /> : loading || !data ? (
        <PageState title="Načítám reklamky" loading />
      ) : (
        <div className="rondo-arc-reklamky-market-grid">
          {(['ALL', 'CZ', 'SK', 'RO'] as Country[]).map((c) => (
            <MtdBox key={c} country={c} m={data.countries[c]} />
          ))}
        </div>
      )}
    </>
  );
}

function MtdBox({ country, m }: { country: Country; m: AdRevCountryMtd }) {
  const meta = COUNTRY_META[country];
  return (
    <Card title={`${meta.flag} ${meta.name}`} description={meta.host || 'Všechny trhy'} className="rondo-arc-reklamky-market-card">
      <div className="rondo-arc-reklamky-revenue"><small>Tržby MTD</small><strong>{fmtKc(m.revenue)}</strong><p>Proti minulému měsíci {fmtDelta(m.revenue, m.prevRevenue)} <span>{fmtKc(m.prevRevenue)}</span></p></div>
      <div className="rondo-arc-reklamky-market-meta"><div><small>Zobrazení</small><strong>{fmtN(m.impressions)}</strong></div><div><small>eCPM</small><strong>{ecpmOf(m.revenue, m.impressions)}</strong></div></div>
    </Card>
  );
}

type ChartRange = '7d' | '30d' | '90d' | 'mtd';
const RANGE_LABELS: Record<ChartRange, string> = {
  '7d': 'Posledních 7 dní', '30d': 'Posledních 30 dní', '90d': 'Posledních 90 dní', mtd: 'Aktuální měsíc (MTD)',
};

function PrehledTab({ partners }: { partners: AdRevPartner[] }) {
  const [partner, setPartner] = useState('all');
  const [country, setCountry] = useState<Country>('ALL');
  const [range, setRange] = useState<ChartRange>('30d');
  const [data, setData] = useState<AdRevTimeseriesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getFinanceReklamkyTimeseries({ partner, country, granularity: 'day' })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [partner, country]);

  const periods = (() => {
    const all = data?.periods ?? [];
    if (range === 'mtd') {
      const now = new Date();
      const prefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      return all.filter((p) => p.key.startsWith(prefix));
    }
    return all.slice(-(range === '7d' ? 7 : range === '30d' ? 30 : 90));
  })();
  const kpi = periods.reduce((a, p) => ({ rev: a.rev + p.revenue, imp: a.imp + p.impressions }), { rev: 0, imp: 0 });

  return (
    <>
      <ServiceBar variant="seg" selects={[
        { label: 'Dodavatel', value: partner, onChange: setPartner, options: partnerOptions(partners) },
        { label: 'Mutace', value: country, onChange: (v) => setCountry(v as Country), options: countryOptions },
        { label: 'Rozsah', value: range, onChange: (v) => setRange(v as ChartRange), options: [{ value: '7d', label: '7 dní' }, { value: '30d', label: '30 dní' }, { value: '90d', label: '90 dní' }, { value: 'mtd', label: 'Měsíc kalend.' }] },
      ]} onReset={() => { setPartner('all'); setCountry('ALL'); setRange('30d'); }} right={<div className="reklamky-filter-meta">{loading ? 'Načítám…' : `${COUNTRY_LABELS[country]} · ${RANGE_LABELS[range]}`}</div>} />

      <div className="rondo-arc-reklamky-kpis">
        <MetricCard label="Tržby" value={kpi.rev} suffix={'\u00a0Kč'} decimals={2} context={RANGE_LABELS[range]} />
        <MetricCard label="Zobrazení" value={kpi.imp} context={COUNTRY_LABELS[country]} />
        <MetricCard label="eCPM" value={kpi.imp > 0 ? (kpi.rev / kpi.imp) * 1000 : 0} suffix={'\u00a0Kč'} decimals={2} context="Za 1 000 zobrazení" />
      </div>

      {error ? <PageState title="Graf se nepodařilo načíst" message={error} /> : (
        <Card title="Tržby a zobrazení" description={RANGE_LABELS[range]} className="rondo-arc-reklamky-chart-card">
          {loading ? <PageState title="Načítám vývoj v čase" loading />
            : periods.length === 0 ? <p>Žádná data.</p>
            : <ReklamkyChart periods={periods} />}
        </Card>
      )}
    </>
  );
}

function ReklamkyChart({ periods }: { periods: AdRevPeriod[] }) {
  const data = {
    labels: periods.map((p) => p.label.slice(0, 5)),
    datasets: [
      { label: 'Tržby (Kč)', data: periods.map((p) => p.revenue), borderColor: '#d86c18', backgroundColor: 'rgba(216,108,24,0.08)', borderWidth: 2.5, fill: true, tension: 0.28, pointRadius: 1.5, pointHoverRadius: 5, yAxisID: 'y' },
      { label: 'Zobrazení', data: periods.map((p) => p.impressions), borderColor: '#16804a', backgroundColor: 'transparent', borderWidth: 2, fill: false, tension: 0.28, pointRadius: 1.5, pointHoverRadius: 5, yAxisID: 'y2' },
    ],
  };
  const options: any = {
    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, position: 'bottom', labels: { font: { size: 11 }, usePointStyle: true, pointStyle: 'line' } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${ctx.dataset.yAxisID === 'y' ? nf.format(ctx.parsed.y) + ' Kč' : nf.format(ctx.parsed.y)}` } },
    },
    scales: {
      x: { ticks: { font: { size: 11 }, maxTicksLimit: 12 } },
      y: { position: 'left', ticks: { font: { size: 11 }, color: '#d86c18', callback: (v: any) => nf.format(v) + ' Kč' }, title: { display: true, text: 'Tržby (Kč)', font: { size: 10 }, color: '#d86c18' } },
      y2: { position: 'right', grid: { drawOnChartArea: false }, ticks: { font: { size: 11 }, color: '#16804a', callback: (v: any) => nf.format(v) }, title: { display: true, text: 'Zobrazení', font: { size: 10 }, color: '#16804a' } },
    },
  };
  return <div style={{ height: 380, position: 'relative' }}><Line data={data} options={options} /></div>;
}

interface VypisRow {
  key: string;
  label: string;
  partnerName: string;
  impressions: number;
  revenue: number;
  ecpm: number;
  isFinal: boolean;
}

function VypisTab({ partners }: { partners: AdRevPartner[] }) {
  const [partner, setPartner] = useState('all');
  const [country, setCountry] = useState<Country>('ALL');
  const [granularity, setGranularity] = useState<AdRevGranularity>('day');
  const [data, setData] = useState<AdRevTimeseriesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const merged = partner === 'merged';
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getFinanceReklamkyTimeseries({ partner: merged ? 'all' : partner, country, granularity })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [partner, merged, country, granularity]);

  const rows: VypisRow[] = [];
  for (const p of [...(data?.periods ?? [])].reverse()) {
    if (merged) {
      rows.push({ key: p.key, label: p.label, partnerName: 'Sloučeno', impressions: p.impressions, revenue: p.revenue, ecpm: p.ecpm, isFinal: p.isFinal });
    } else {
      for (const s of p.byPartner) {
        rows.push({ key: `${p.key}|${s.partner}`, label: p.label, partnerName: s.partnerName, impressions: s.impressions, revenue: s.revenue, ecpm: s.ecpm, isFinal: s.isFinal });
      }
    }
  }
  const tot = rows.reduce((a, r) => ({ imp: a.imp + r.impressions, rev: a.rev + r.revenue }), { imp: 0, rev: 0 });
  const tableRows = rows.map((row) => ({ ...row }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'key', label: 'Období', width: 160, render: (_, row) => <span>{row.label}{!row.isFinal && <Dot />}</span> },
    { key: 'partnerName', label: 'Poskytovatel', width: 220 },
    { key: 'impressions', label: 'Zobrazení', numeric: true, width: 150, render: (_, row) => fmtN(row.impressions) },
    { key: 'ecpm', label: 'eCPM', numeric: true, width: 130, render: (_, row) => row.impressions > 0 ? fmtKc(row.ecpm) : '—' },
    { key: 'revenue', label: 'Tržby', numeric: true, width: 170, render: (_, row) => fmtKc(row.revenue) },
  ];

  return (
    <>
      <ServiceBar variant="seg" selects={[
        { label: 'Dodavatel', value: partner, onChange: setPartner, options: partnerOptions(partners, true) },
        { label: 'Mutace', value: country, onChange: (v) => setCountry(v as Country), options: countryOptions },
        { label: 'Granularita', value: granularity, onChange: (v) => setGranularity(v as AdRevGranularity), options: [{ value: 'day', label: 'Den' }, { value: 'month', label: 'Měsíc' }] },
      ]} onReset={() => { setPartner('all'); setCountry('ALL'); setGranularity('day'); }} right={<div className="reklamky-filter-meta">{loading ? 'Načítám…' : `${rows.length} řádků`}</div>} />

      {!loading && !error && <div className="rondo-arc-reklamky-kpis">
        <MetricCard label="Zobrazení" value={tot.imp} context="Ve výpisu" />
        <MetricCard label="Tržby" value={tot.rev} suffix={'\u00a0Kč'} decimals={2} context="Ve výpisu" />
        <MetricCard label="eCPM" value={tot.imp > 0 ? (tot.rev / tot.imp) * 1000 : 0} suffix={'\u00a0Kč'} decimals={2} context="Za 1 000 zobrazení" />
      </div>}
      <div className="rondo-arc-reklamky-table">
        {error && <PageState title="Výpis se nepodařilo načíst" message={error} />}
        {loading ? <PageState title="Načítám výpis" loading /> : !error && <SortableDataTable rows={tableRows} columns={columns} rowKey="key" caption="Reklamní tržby" emptyMessage="Žádná data." />}
      </div>
      <p className="rondo-arc-reklamky-note"><Dot /> Nefinální období — dodavatel dopočítává přibližně čtyři dny zpětně.</p>
    </>
  );
}
